// ---- Status-effect presentation catalog ----
// Client labels / icons / tooltip copy for lasting combat CC (snare, stun, slow).
// Mechanics live in src/sim/status.ts — this is the HUD surface only.
// Knockback is an impulse, not a lasting status chip (see status.ts header).

export type StatusKind = "snare" | "stun" | "slow";

export type StatusDef = {
  kind: StatusKind;
  /** Glyph shown on overhead icons and focus chips. */
  icon: string;
  /** Card title. */
  name: string;
  /** Hover tooltip — what the effect does to the target. */
  desc: string;
  /** Accent border / glow on the focus chip. */
  color: string;
};

/** Stable display order (stun first — the stricter gate). Append new kinds here
 *  so multi-effect stacks stay deterministic. */
export const STATUS_ORDER: readonly StatusKind[] = ["stun", "snare", "slow"];

export const STATUS_DEFS: Record<StatusKind, StatusDef> = {
  stun: {
    kind: "stun",
    icon: "💫",
    name: "Stunned",
    desc: "Cannot move or act — locked out of attacks, blocks, and abilities.",
    color: "#8ab4ff",
  },
  snare: {
    kind: "snare",
    icon: "⛓",
    name: "Snared",
    desc: "Cannot move. Can still attack, block, and use abilities.",
    color: "#d4b06a",
  },
  slow: {
    kind: "slow",
    icon: "🐌",
    name: "Slowed",
    desc: "Moves slower. Can still attack, block, and use abilities.",
    color: "#5ec8e8",
  },
};

/** Active lasting statuses on a focus target (booleans from the wire). */
export function activeStatuses(flags: { snare?: boolean; stun?: boolean; slow?: boolean }): StatusDef[] {
  const out: StatusDef[] = [];
  for (const k of STATUS_ORDER) {
    if (flags[k]) out.push(STATUS_DEFS[k]);
  }
  return out;
}
